// release-recipe-schema.ts — `config.release.deploy_recipe` / `verify_recipe`.
//
// The ticket verification recipe contract (verification-schema.ts) plus one
// rule (close-out A3, Luke Q2 2026-09-30): `cwd` must stay inside the mission
// worktree. A release recipe runs AFTER G3 approved one candidate, and
// `resolveRecipeCwdAgainstRepoRoot` keeps an absolute cwd as-is — so an
// absolute cwd, or a relative one climbing out with `..`, deployed whatever
// tree lived there, not the candidate. Refused at config load (`config_invalid`
// before any side effect). Deploying from another repo means wrapping that step
// in a script committed inside this one.
//
// Kept as a regex (not a zod refine) so the rule survives into the generated
// config.schema.json `pattern` the pack ships — a refine would vanish there.
import { z } from "zod";
import { VerificationRecipeSchema } from "./verification-schema.js";

/**
 * Relative, no `..` path segment anywhere (either separator), no control
 * character (review P2 L1: NUL made spawn throw ⇒ an unknown deploy outcome):
 * rejects `/x`, `\x`, `C:/x`, `..`, `../x`, `a/../../x`, `a\u0000b`; accepts
 * `.`, `scripts`, `..hidden`, `a..b`.
 */
export const RELEASE_RECIPE_CWD_PATTERN =
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point (review P2 L1)
  /^(?![\\/])(?![A-Za-z]:)(?!.*[\u0000-\u001f])(?!(?:.*[\\/])?\.\.(?:[\\/]|$)).+$/;

export const ReleaseRecipeSchema = VerificationRecipeSchema.extend({
  cwd: z
    .string()
    .min(1)
    .regex(
      RELEASE_RECIPE_CWD_PATTERN,
      "cwd của recipe release phải là đường dẫn TƯƠNG ĐỐI trong worktree mission (không tuyệt đối, không `..`) — deploy phải chạy trên đúng candidate G3 đã duyệt.",
    ),
}).strict();

/**
 * A4 (merged plan 20260930-0830, Luke Q3): per-recipe run cap for release
 * deploy/smoke, in seconds. Absent ⇒ the beta default below. The ceiling keeps
 * a hung recipe from holding `run` (and the mission lock) indefinitely.
 */
export const DEFAULT_RELEASE_RECIPE_TIMEOUT_SEC = 60;
export const MAX_RELEASE_RECIPE_TIMEOUT_SEC = 3600;

export const ReleaseRecipeTimeoutSecSchema = z
  .number()
  .int()
  .min(1)
  .max(MAX_RELEASE_RECIPE_TIMEOUT_SEC);
